import { useCallback, useEffect, useState } from "react";
import { ArrowRight, CircleCheck, CircleX, History, Loader2, Play } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, DecisionChip, Empty, Loading, PageHeader, Panel, SeverityChip, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime, money } from "@/lib/format";

const PRESETS = {
  "Indian cash gift over ₹2,000": {
    global: "GLOBAL", regional_overlay: "FATF", country: "IN", legal_form: "trust",
    charity_classification: "humanitarian", tax_status: "tax_exempt", activity: "fundraising",
    fund_source: "domestic", transaction_type: "donation", beneficiary_data_class: "none",
    amount: 25000, base_amount: 25000, currency: "INR", payment_method: "cash",
    donor_type: "individual", donor_country: "IN", cross_border: false,
    donor_kyc_verified: true, sanctions_hit: false, receipt_required: true,
  },
  "Foreign contribution without FCRA account": {
    global: "GLOBAL", regional_overlay: "FATF", country: "IN", legal_form: "trust",
    charity_classification: "humanitarian", tax_status: "tax_exempt", activity: "fundraising",
    fund_source: "foreign", transaction_type: "donation", beneficiary_data_class: "none",
    amount: 40000, base_amount: 4280000, currency: "GBP", payment_method: "bank_transfer",
    donor_type: "institutional", donor_country: "GB", cross_border: true,
    has_fcra_account: false, donor_kyc_verified: true, sanctions_hit: false, receipt_required: true,
  },
  "Sanctioned counterparty": {
    global: "GLOBAL", regional_overlay: "FATF", country: "IN", legal_form: "trust",
    charity_classification: "humanitarian", tax_status: "tax_exempt", activity: "fundraising",
    fund_source: "foreign", transaction_type: "donation", beneficiary_data_class: "none",
    amount: 100000, base_amount: 8400000, currency: "USD", payment_method: "bank_transfer",
    donor_type: "corporate", donor_country: "RU", cross_border: true,
    sanctions_hit: true, pep: true, donor_kyc_verified: false, receipt_required: true,
  },
  "Corporate CSR gift ₹25L": {
    global: "GLOBAL", regional_overlay: "FATF", country: "IN", legal_form: "trust",
    charity_classification: "humanitarian", tax_status: "tax_exempt", activity: "fundraising",
    fund_source: "domestic", transaction_type: "donation", beneficiary_data_class: "none",
    amount: 2500000, base_amount: 2500000, currency: "INR", payment_method: "bank_transfer",
    donor_type: "corporate", donor_country: "IN", cross_border: false,
    donor_kyc_verified: true, sanctions_hit: false, receipt_required: true,
  },
  "Restricted fund spent off-purpose": {
    global: "GLOBAL", regional_overlay: "FATF", country: "IN", legal_form: "trust",
    charity_classification: "humanitarian", tax_status: "tax_exempt", activity: "expenditure",
    fund_source: "domestic", transaction_type: "expenditure", beneficiary_data_class: "none",
    amount: 400000, base_amount: 400000, currency: "INR", fund_type: "restricted",
    purpose_code: "office_furniture", purpose_permitted: false, exceeds_fund_balance: false,
  },
  "Clean UK Gift Aid donation": {
    global: "GLOBAL", regional_overlay: "EU", country: "GB", legal_form: "foundation",
    charity_classification: "humanitarian", tax_status: "tax_exempt", activity: "fundraising",
    fund_source: "domestic", transaction_type: "donation", beneficiary_data_class: "none",
    amount: 500, base_amount: 53500, currency: "GBP", payment_method: "card",
    donor_type: "individual", donor_country: "GB", cross_border: false,
    donor_kyc_verified: true, sanctions_hit: false, receipt_required: true,
  },
};

export default function DecisionSimulator() {
  const [ctx, setCtx] = useState(PRESETS["Indian cash gift over ₹2,000"]);
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [replay, setReplay] = useState(null);

  const loadRecent = useCallback(() => {
    api.get("/regulatory/decisions?limit=20").then((r) => setRecent(r.data)).catch(() => setRecent([]));
  }, []);

  useEffect(loadRecent, [loadRecent]);

  const run = async () => {
    setBusy(true);
    setReplay(null);
    try {
      const { data } = await api.post("/regulatory/decisions/evaluate", {
        context: { ...ctx, transaction_date: asOf }, transaction_date: asOf, persist: true,
      });
      setResult(data);
      loadRecent();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const doReplay = async (id) => {
    try {
      const { data } = await api.post(`/regulatory/decisions/${id}/replay`);
      setReplay(data);
      setResult(data.replay);
      toast[data.identical ? "success" : "warning"](
        data.identical ? "Historical replay reproduced the original decision"
          : `Replay differs: ${data.original_decision} → ${data.replay.decision}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setField = (k, v) => setCtx((c) => ({ ...c, [k]: v }));

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Regulatory Decision API · /api/regulatory/decisions/evaluate"
        title="Decision Simulator"
        description="Submit a transaction context and see the aggregate decision, the exact cascade path taken and every rule version that fired — persisted for audit and historical replay."
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[380px_1fr]">
        <div className="space-y-4">
          <Panel testId="panel-presets" kicker="Scenarios" title="Load a preset context">
            <div className="flex flex-col gap-1.5 p-3">
              {Object.keys(PRESETS).map((k) => (
                <button key={k} onClick={() => { setCtx(PRESETS[k]); setResult(null); setReplay(null); }}
                        data-testid={`preset-${k.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`}
                        className="flex items-center justify-between rounded border border-border px-3 py-2 text-left text-xs text-foreground/80 transition-colors duration-150 hover:border-primary/40 hover:bg-primary/5">
                  {k} <ArrowRight className="h-3 w-3 text-muted-foreground/70" />
                </button>
              ))}
            </div>
          </Panel>

          <Panel testId="panel-context" kicker="Transaction context" title="Payload">
            <div className="space-y-3 p-4">
              <div className="grid grid-cols-2 gap-3">
                <Row label="Country">
                  <Input value={ctx.country || ""} onChange={(e) => setField("country", e.target.value.toUpperCase())}
                         data-testid="sim-country-input" className="h-8 bg-muted/40 font-mono text-xs" />
                </Row>
                <Row label="Transaction type">
                  <Select value={ctx.transaction_type || "donation"} onValueChange={(v) => setField("transaction_type", v)}>
                    <SelectTrigger data-testid="sim-txntype-select" className="h-8 bg-muted/40 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["donation", "expenditure", "disbursement", "transfer"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Amount">
                  <Input type="number" value={ctx.amount ?? 0}
                         onChange={(e) => setField("amount", Number(e.target.value))}
                         data-testid="sim-amount-input" className="h-8 bg-muted/40 font-mono text-xs" />
                </Row>
                <Row label="Base amount (INR)">
                  <Input type="number" value={ctx.base_amount ?? 0}
                         onChange={(e) => setField("base_amount", Number(e.target.value))}
                         data-testid="sim-base-amount-input" className="h-8 bg-muted/40 font-mono text-xs" />
                </Row>
                <Row label="Currency">
                  <Input value={ctx.currency || "INR"} onChange={(e) => setField("currency", e.target.value.toUpperCase())}
                         data-testid="sim-currency-input" className="h-8 bg-muted/40 font-mono text-xs" />
                </Row>
                <Row label="Fund source">
                  <Select value={ctx.fund_source || "domestic"} onValueChange={(v) => setField("fund_source", v)}>
                    <SelectTrigger data-testid="sim-fundsource-select" className="h-8 bg-muted/40 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["domestic", "foreign", "mixed"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Donor type">
                  <Select value={ctx.donor_type || "individual"} onValueChange={(v) => setField("donor_type", v)}>
                    <SelectTrigger data-testid="sim-donortype-select" className="h-8 bg-muted/40 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["individual", "corporate", "institutional", "anonymous"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Payment method">
                  <Select value={ctx.payment_method || "bank_transfer"} onValueChange={(v) => setField("payment_method", v)}>
                    <SelectTrigger data-testid="sim-payment-select" className="h-8 bg-muted/40 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["bank_transfer", "cash", "card", "upi", "cheque"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Evaluation date (as-of)">
                  <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
                         data-testid="sim-asof-input" className="h-8 bg-muted/40 font-mono text-xs" />
                </Row>
              </div>

              <div className="space-y-2 border-t border-border pt-3">
                {[
                  ["cross_border", "Cross-border"],
                  ["sanctions_hit", "Sanctions / PEP hit"],
                  ["donor_kyc_verified", "Donor KYC verified"],
                  ["has_fcra_account", "Active FCRA designated account"],
                  ["purpose_permitted", "Purpose permitted by fund restriction"],
                  ["exceeds_fund_balance", "Exceeds fund balance"],
                  ["receipt_required", "Receipt required"],
                ].map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{label}</span>
                    <Switch checked={Boolean(ctx[key])} onCheckedChange={(v) => setField(key, v)}
                            data-testid={`sim-toggle-${key}`} />
                  </div>
                ))}
              </div>

              <Button onClick={run} disabled={busy} data-testid="decision-simulate-button"
                      className="w-full rounded-full bg-primary font-medium text-white hover:bg-primary/90">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                Evaluate transaction
              </Button>
            </div>
          </Panel>
        </div>

        <div className="space-y-4">
          {!result && (
            <Panel testId="panel-decision-empty">
              <div className="p-12 text-center">
                <div className="kicker mb-3">Awaiting evaluation</div>
                <p className="mx-auto max-w-md text-sm text-muted-foreground">
                  Pick a scenario, adjust the context and run the engine. The decision, cascade path and
                  rule trace will appear here.
                </p>
              </div>
            </Panel>
          )}

          {result && (
            <>
              <div className={`panel relative overflow-hidden p-5 ${result.decision === "BLOCK" ? "alarm border-red-500/30" : ""}`}
                   data-testid="decision-result-banner">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="kicker mb-2">Aggregate decision · precedence block › require_approval › warn › inform</div>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-3xl font-bold tracking-tight text-foreground"
                            data-testid="decision-value">
                        {result.decision.replace(/_/g, " ")}
                      </span>
                      <DecisionChip decision={result.decision} testId="decision-chip" />
                    </div>
                  </div>
                  <div className="text-right font-mono text-[11px] text-muted-foreground">
                    <div>as-of {result.as_of}</div>
                    <div>{result.rules_evaluated} effective rule versions evaluated</div>
                    {result.decision_id && <div className="text-muted-foreground/70">id {result.decision_id.slice(-8)}</div>}
                  </div>
                </div>
                {replay && (
                  <div className="mt-3 flex items-center gap-2 border-t border-border pt-3 text-xs"
                       data-testid="replay-banner">
                    <History className="h-3.5 w-3.5 text-primary" />
                    <span className="text-muted-foreground">
                      Historical replay of {replay.original_as_of}: original <b className="text-foreground">{replay.original_decision}</b>,
                      replayed <b className="text-foreground">{replay.replay.decision}</b>
                    </span>
                    {replay.identical
                      ? <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">Reproduced</Chip>
                      : <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">Divergent</Chip>}
                  </div>
                )}
              </div>

              <Panel testId="panel-triggered" kicker="Rule trace" title={`${result.triggered.length} rule version(s) fired`}>
                {result.triggered.length === 0 && (
                  <div className="p-6 text-xs text-muted-foreground">No rule fired — the transaction is clear to proceed.</div>
                )}
                <div className="divide-y divide-border">
                  {result.triggered.map((t) => (
                    <div key={`${t.rule_key}-${t.version}`} className="p-4" data-testid={`triggered-rule-${t.rule_key}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-[11px] text-primary">{t.rule_key}</span>
                            <span className="font-mono text-[10px] text-muted-foreground/70">v{t.version}</span>
                            <SeverityChip severity={t.effective_severity} testId={`fired-severity-${t.rule_key}`} />
                            {t.declared_severity !== t.effective_severity && (
                              <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                                downgraded from {t.declared_severity}
                              </Chip>
                            )}
                          </div>
                          <div className="mt-1.5 text-sm text-foreground">{t.title}</div>
                          <div className="font-mono text-[11px] text-muted-foreground">{t.citation}</div>
                          {t.obligation && <p className="mt-2 text-xs text-muted-foreground">{t.obligation}</p>}
                          {t.remediation && (
                            <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400/80">Remediation: {t.remediation}</p>
                          )}
                          {(t.notes || []).map((n) => (
                            <p key={n} className="mt-1 text-[11px] text-amber-600 dark:text-amber-400/90">{n}</p>
                          ))}
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground/70">
                          {t.matched_layers}/12 layers matched
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                        <div className="rounded border border-border bg-muted/40 p-2.5">
                          <div className="kicker mb-1.5">Cascade path</div>
                          <div className="flex flex-wrap gap-1">
                            {t.cascade_path.map((p) => (
                              <span key={p.key}
                                    data-testid={`trace-layer-${t.rule_key}-${p.layer}`}
                                    className={`rounded px-1.5 py-0.5 font-mono text-[9px] ${
                                      p.wildcard ? "bg-muted text-muted-foreground"
                                        : p.matched ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-red-500/10 text-red-700 dark:text-red-300"}`}>
                                {String(p.layer).padStart(2, "0")} {p.key}
                                {!p.wildcard && `=${Array.isArray(p.expected) ? p.expected.join("|") : p.expected}`}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="rounded border border-border bg-muted/40 p-2.5">
                          <div className="kicker mb-1.5">Condition evaluation</div>
                          <div className="space-y-1">
                            {(t.condition_trace || []).length === 0 && (
                              <div className="font-mono text-[10px] text-muted-foreground/70">applicability-only rule</div>
                            )}
                            {(t.condition_trace || []).map((c, i) => (
                              <div key={i} className="flex items-start gap-1.5 font-mono text-[10px]">
                                {c.result ? <CircleCheck className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600 dark:text-emerald-400" />
                                  : <CircleX className="mt-0.5 h-3 w-3 shrink-0 text-red-600 dark:text-red-400" />}
                                <span className="text-muted-foreground">{c.expression}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {t.gates && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {Object.entries(t.gates).map(([g, ok]) => (
                            <Chip key={g} className={ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                              : "border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400"}>
                              {g.replace(/_/g, " ")}
                            </Chip>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Panel>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Panel testId="panel-satisfied" kicker="Applicable but satisfied" title={`${result.satisfied.length} rules`}>
                  <div className="max-h-[240px] overflow-y-auto divide-y divide-border">
                    {result.satisfied.length === 0 && <div className="p-4 text-xs text-muted-foreground/70">None</div>}
                    {result.satisfied.map((s) => (
                      <div key={`${s.rule_key}-${s.version}`} className="px-4 py-2">
                        <div className="font-mono text-[11px] text-muted-foreground">{s.rule_key} v{s.version}</div>
                        <div className="text-[11px] text-muted-foreground">{s.title}</div>
                      </div>
                    ))}
                  </div>
                </Panel>
                <Panel testId="panel-not-applicable" kicker="Out of scope" title={`${result.not_applicable.length} rules`}>
                  <div className="max-h-[240px] overflow-y-auto divide-y divide-border">
                    {result.not_applicable.map((s) => {
                      const failed = s.cascade_path.find((p) => !p.matched);
                      return (
                        <div key={`${s.rule_key}-${s.version}`} className="px-4 py-2">
                          <div className="font-mono text-[11px] text-muted-foreground">{s.rule_key} v{s.version}</div>
                          <div className="font-mono text-[10px] text-muted-foreground/70">
                            stopped at layer {failed?.layer} · {failed?.key} expected{" "}
                            {Array.isArray(failed?.expected) ? failed.expected.join("|") : String(failed?.expected)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Panel>
              </div>
            </>
          )}

          <Panel testId="panel-recent-decisions" kicker="Decision log" title="Recent evaluations">
            <Table testId="recent-decisions-table"
                   head={<><Th>When</Th><Th>Decision</Th><Th>Source</Th><Th>Value</Th><Th>Fired</Th><Th /></>}>
              {recent.length === 0 && <Empty colSpan={6} label="No decisions logged yet" />}
              {recent.map((d) => (
                <tr key={d.id} className="row-hover" data-testid={`decision-row-${d.id}`}>
                  <Td className="whitespace-nowrap font-mono text-[11px]">{dateTime(d.created_at)}</Td>
                  <Td><DecisionChip decision={d.decision} /></Td>
                  <Td className="font-mono text-[10px] text-muted-foreground">{d.source}</Td>
                  <Td className="font-mono text-[11px]">
                    {d.context?.amount ? money(d.context.amount, d.context.currency || "INR", true) : "—"}
                  </Td>
                  <Td className="font-mono text-[11px]">{(d.triggered || []).length}</Td>
                  <Td>
                    <Button size="sm" variant="outline" onClick={() => doReplay(d.id)}
                            data-testid={`replay-decision-${d.id}`}
                            className="h-7 rounded-full border-border px-2.5 text-[10px]">
                      <History className="mr-1 h-3 w-3" /> Replay
                    </Button>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
